// Portfolio Administration — Loans list-page filter row. Multi-select chips
// for Portfolio (real names from the registry) / AccountState / Days late
// (applied client-side over the real schedule-derived days from the batch
// health-grades payload), plus a free-text search box and the "Assigned B1"
// staff-user typeahead (UserPicker — no raw user ids typed by humans).
// State lives in the parent (URL-bound) so this component is purely
// presentational.

import { Input } from "@/design-system/components/ui";
import { UserPicker } from "@/components/users/UserPicker";
import { FilterChipGroup } from "@/components/portfolioAdmin/registry/FilterChipGroup";
import {
  LOAN_ACCOUNT_STATES,
  LOAN_DAYS_LATE_BUCKETS,
} from "@/api/portfolioAdmin/types";

const STATE_OPTIONS = LOAN_ACCOUNT_STATES.map((value) => ({
  value,
  label: value,
}));

const DAYS_LATE_OPTIONS = LOAN_DAYS_LATE_BUCKETS.map((value) => ({
  value,
  label: value === "0" ? "0 days" : `${value} days`,
}));

export interface LoansListFiltersProps {
  portfolioIds: string[];
  portfolioOptions: ReadonlyArray<{ value: string; label: string }>;
  accountStates: string[];
  daysLateBuckets: string[];
  assignedB1UserId: string;
  assignedB1UserName: string;
  search: string;
  onPortfolioIdsChange: (next: string[]) => void;
  onAccountStatesChange: (next: string[]) => void;
  onDaysLateBucketsChange: (next: string[]) => void;
  onAssignedB1Change: (userId: string | null, displayName: string) => void;
  onSearchChange: (next: string) => void;
  onClear: () => void;
}

export function LoansListFilters({
  portfolioIds,
  portfolioOptions,
  accountStates,
  daysLateBuckets,
  assignedB1UserId,
  assignedB1UserName,
  search,
  onPortfolioIdsChange,
  onAccountStatesChange,
  onDaysLateBucketsChange,
  onAssignedB1Change,
  onSearchChange,
  onClear,
}: LoansListFiltersProps) {
  const hasActive =
    portfolioIds.length > 0 ||
    accountStates.length > 0 ||
    daysLateBuckets.length > 0 ||
    assignedB1UserId.length > 0 ||
    search.length > 0;
  return (
    <>
      <div className="flex flex-col gap-space-1 min-w-[240px]">
        <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
          Search
        </span>
        <Input
          type="search"
          value={search}
          placeholder="Search by loan ID or borrower name…"
          onChange={(e) => onSearchChange(e.target.value)}
          data-testid="loans-filter-search"
        />
      </div>
      {portfolioOptions.length > 0 ? (
        <FilterChipGroup
          label="Portfolio"
          testId="loans-filter-portfolio"
          options={portfolioOptions}
          selected={portfolioIds}
          onChange={onPortfolioIdsChange}
        />
      ) : null}
      <FilterChipGroup
        label="Status"
        testId="loans-filter-status"
        options={STATE_OPTIONS}
        selected={accountStates}
        onChange={onAccountStatesChange}
      />
      <FilterChipGroup
        label="Days late"
        testId="loans-filter-days-late"
        options={DAYS_LATE_OPTIONS}
        selected={daysLateBuckets}
        onChange={onDaysLateBucketsChange}
      />
      <div
        className="flex flex-col gap-space-1 min-w-[200px]"
        data-testid="loans-filter-b1"
      >
        <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
          Assigned B1
        </span>
        <UserPicker
          value={assignedB1UserId || null}
          initialDisplayName={assignedB1UserName}
          allowUnassigned={false}
          ariaLabel="Filter by assigned B1"
          placeholder="Any B1 — search by name…"
          onChange={onAssignedB1Change}
        />
      </div>
      {hasActive ? (
        <button
          type="button"
          className="text-small text-gold-shadow hover:underline self-end pb-[6px]"
          onClick={onClear}
          data-testid="loans-filter-clear"
        >
          Clear filters
        </button>
      ) : null}
    </>
  );
}
